import CartModifier from 'models/CartModifier';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import CartItem from 'models/CartItem';
import SharedCartItem from 'models/SharedCartItem';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import useQuery from 'hooks/use-query';

export interface ItemSheetProviderContextProps {
  processing: boolean;
  setProcessing: (processing: boolean) => void;
  submitPressed: boolean;
  setSubmitPressed: (result: boolean) => void;
  /**
   * this is the item that's being added to cart / updated
   */
  cartItem: CartItem | SharedCartItem;
  pushCartItem: (cartItem: CartItem | SharedCartItem) => void;
  closeModal: () => void;
  /**
   * this is what the modal is currently displaying. it could be a modifier
   */
  displayedItem: CartItem | CartModifier;
  setDisplayedItem: (displayedItem: CartItem | CartModifier) => void;
  htmlElementErrors: (HTMLInputElement | HTMLDivElement)[];
  addHTMLElementError: (htmlElement: HTMLInputElement | HTMLDivElement) => void;
  removeHTMLElementError: (htmlElement: HTMLInputElement | HTMLDivElement) => void;
  modsWithMissingSelections: (CartItem | CartModifier)[];
  addModWithMissingSelections: (modifier: CartItem | CartModifier) => void;
  removeModWithMissingSelections: (modifier: CartItem | CartModifier) => void;
}

// @ts-expect-error
export const ItemSheetProviderContext = React.createContext<ItemSheetProviderContextProps>(undefined);

function ItemSheetProvider({ children, cartItem }: { cartItem: CartItem | SharedCartItem; children: ReactNode }) {
  const [processing, setProcessing] = useState(false);
  const [submitPressed, setSubmitPressed] = useState(false);
  const [displayedItem, setDisplayedItem] = useState<CartItem | CartModifier>(cartItem);
  const [cartItems, setCartItems] = useState<CartItem[] | SharedCartItem[]>([cartItem]);
  const [htmlElementErrors, setHtmlElementErrors] = useState<(HTMLInputElement | HTMLDivElement)[]>([]);
  const [modsWithMissingSelections, setModsWithMissingSelections] = useState<(CartItem | CartModifier)[]>([]);
  const history = useHistory();
  const query = useQuery();

  const addHTMLElementError = useCallback(
    (htmlElement: HTMLInputElement | HTMLDivElement) => {
      if (!htmlElementErrors.includes(htmlElement)) {
        setHtmlElementErrors((prev) => [...prev, htmlElement]);
      }
    },
    [htmlElementErrors]
  );

  const removeHTMLElementError = useCallback((htmlElement: HTMLInputElement | HTMLDivElement) => {
    if (htmlElementErrors.includes(htmlElement)) {
      setHtmlElementErrors((prev) => prev.filter((element) => element !== htmlElement));
    }
  }, []);

  const addModWithMissingSelections = useCallback(
    (modifier: CartItem | CartModifier) => {
      if (!modsWithMissingSelections.includes(modifier)) {
        setModsWithMissingSelections((prev) => [...prev, modifier]);
      }
    },
    [modsWithMissingSelections]
  );

  const removeModWithMissingSelections = useCallback((modifier: CartItem | CartModifier) => {
    if (modsWithMissingSelections.includes(modifier)) {
      setModsWithMissingSelections((prev) => prev.filter((mod) => mod !== modifier));
    }
  }, []);

  const updateDisplayedItem = useCallback(
    (newItem: CartItem | CartModifier) => {
      setSubmitPressed(false);
      setDisplayedItem(() => newItem);
      setHtmlElementErrors(() => []);
      setTimeout(() => document.querySelector('.ant-modal-wrap')?.scrollTo({ top: 0 }));
    },
    [setSubmitPressed, setDisplayedItem]
  );

  const pushCartItem = useCallback(
    (newItem: CartItem | SharedCartItem) => {
      setCartItems((prev) => [...prev, newItem]);
      updateDisplayedItem(newItem);
      query.set('item', newItem.menu_item_id);
      history.push({ search: query.toString() });
    },

    [updateDisplayedItem, query, history]
  );

  useEffect(() => {
    const unregisterListener = history.listen((_, action) => {
      if (action === 'POP') {
        setCartItems((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
        updateDisplayedItem(cartItems.length > 1 ? cartItems[cartItems.length - 2] : cartItems[cartItems.length - 1]);
      }
    });

    return unregisterListener;
  }, [history, cartItems]);

  return (
    <ItemSheetProviderContext.Provider
      value={{
        processing,
        setProcessing,
        submitPressed,
        setSubmitPressed,
        cartItem: cartItems[cartItems.length - 1],
        pushCartItem,
        displayedItem,
        setDisplayedItem: updateDisplayedItem,
        htmlElementErrors,
        addHTMLElementError,
        removeHTMLElementError,
        modsWithMissingSelections,
        addModWithMissingSelections,
        removeModWithMissingSelections,
        closeModal: history.goBack,
      }}
    >
      {children}
    </ItemSheetProviderContext.Provider>
  );
}

export default observer(ItemSheetProvider);
